import type {
  VariantCatalog,
  VariantCombination,
  VariantOption,
} from "../types";
import { availabilityRank } from "./availabilityRank";
import { compareCombinations } from "./compareCombinations";
import { matchesPrefix } from "./matchesPrefix";
import { swatchKey } from "./swatchKey";

function bestCombination(matches: VariantCombination[], locale: string) {
  let best = matches[0];
  if (!best) return undefined;

  let maxPrice = best.sellingPrice;
  for (let index = 1; index < matches.length; index += 1) {
    const candidate = matches[index];
    if (!candidate) continue;
    if (candidate.sellingPrice > maxPrice) maxPrice = candidate.sellingPrice;
    if (compareCombinations(candidate, best, locale) < 0) best = candidate;
  }

  return { best, fromPrice: maxPrice > best.sellingPrice };
}

export function getVariantOptions(
  catalog: VariantCatalog,
  selection: readonly string[],
  index: number,
  locale: string,
) {
  const prefix = selection.slice(0, index);
  const grouped = new Map<string, VariantCombination[]>();

  for (const combination of catalog.combinations) {
    if (!matchesPrefix(combination, prefix)) continue;
    const value = combination.values[index];
    if (!value) continue;
    const group = grouped.get(value);
    if (group) group.push(combination);
    else grouped.set(value, [combination]);
  }

  const attribute = catalog.attributes[index];
  const options: VariantOption[] = [];

  for (const [value, matches] of grouped) {
    const picked = bestCombination(matches, locale);
    if (!picked) continue;
    options.push({
      value,
      swatch: attribute
        ? catalog.swatches[swatchKey(attribute.code, value)]
        : undefined,
      sellingPrice: picked.best.sellingPrice,
      listPrice: picked.best.listPrice,
      stock: picked.best.stock,
      stockNumber: picked.best.stockNumber,
      fromPrice: picked.fromPrice,
    });
  }

  options.sort((a, b) => {
    const stock = availabilityRank(a.stock) - availabilityRank(b.stock);
    if (stock !== 0) return stock;
    const price = a.sellingPrice - b.sellingPrice;
    if (price !== 0) return price;
    return a.value.localeCompare(b.value, locale);
  });

  return options;
}
